/**
 * Orca Whirlpool Pool Creator
 * Full SDK integration for creating TOKEN/SOL pools with single-sided liquidity
 * 
 * Flow:
 * 1. Create/Initialize Whirlpool (TOKEN/SOL pair)
 * 2. Initialize required Tick Arrays
 * 3. Open Position with single-sided liquidity (token only)
 */

import { 
    Connection, 
    PublicKey, 
    Keypair,
    Transaction,
    TransactionInstruction,
    SystemProgram,
    SYSVAR_RENT_PUBKEY,
    ComputeBudgetProgram
} from '@solana/web3.js';
import { 
    TOKEN_PROGRAM_ID, 
    NATIVE_MINT,
    getAssociatedTokenAddress,
    createAssociatedTokenAccountInstruction,
    createTransferInstruction,
    ASSOCIATED_TOKEN_PROGRAM_ID
} from '@solana/spl-token';
// @ts-ignore - BN is available via @solana/web3.js or @orca-so/whirlpools-sdk
import BN from 'bn.js';

// ============ CONSTANTS ============

// Orca Whirlpool Program (Mainnet)
export const WHIRLPOOL_PROGRAM_ID = new PublicKey('whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc');

// Whirlpools Config Account (Mainnet)
export const WHIRLPOOLS_CONFIG = new PublicKey('2LecshUwdy9xi7meFgHtFJQNSKk4KdTrcpvaB56dP2NQ');

// SOL (Native Mint / WSOL)
export const SOL_MINT = NATIVE_MINT;

// Fee Tier: 1% (10000 bps) with tick spacing 128 - good for new volatile tokens
export const DEFAULT_FEE_TIER = 128;
export const DEFAULT_TICK_SPACING = 128;

// Standard pool parameters  
// Set current tick ABOVE tickUpper for pure single-sided TEST deposit
// First buy will push price DOWN into the liquidity range
export const STANDARD_SQRT_PRICE_X64 = "118116794080090393390000";  // For tick 175299
export const STANDARD_TICK_INDEX = 175299;  // Above tickUpper - 100% TEST deposit

// Tick Array Size
const TICK_ARRAY_SIZE = 88;

// Min/Max ticks
const MIN_TICK_INDEX = -443636;
const MAX_TICK_INDEX = 443636;

// ============ PRICE/TICK MATH ============

/**
 * Convert price to sqrtPriceX64
 * sqrtPriceX64 = sqrt(price) * 2^64
 * 
 * We use string manipulation to avoid JavaScript's floating point limitations
 * and scientific notation issues with BN.js
 */
export function priceToSqrtPriceX64(price: number): BN {
    // sqrt(price) with high precision
    const sqrtPrice = Math.sqrt(price);
    
    // 2^64 as a BN
    const Q64 = new BN(2).pow(new BN(64));
    
    // To avoid floating point issues, we multiply sqrtPrice by a large factor,
    // convert to BN, then adjust
    const PRECISION = 1e15; // 15 decimal places of precision
    const sqrtPriceScaled = Math.floor(sqrtPrice * PRECISION);
    
    // sqrtPriceX64 = (sqrtPriceScaled / PRECISION) * 2^64
    //              = sqrtPriceScaled * 2^64 / PRECISION
    const result = new BN(sqrtPriceScaled.toString())
        .mul(Q64)
        .div(new BN(PRECISION.toString()));
    
    return result;
}

/**
 * Convert price to tick index
 * tick = floor(log(price) / log(1.0001))
 */
export function priceToTickIndex(price: number): number {
    if (price <= 0) return MIN_TICK_INDEX;
    return Math.floor(Math.log(price) / Math.log(1.0001));
}

/**
 * Get nearest initializable tick
 */
export function getInitializableTickIndex(tick: number, tickSpacing: number): number {
    return Math.floor(tick / tickSpacing) * tickSpacing;
}

/**
 * Get tick array start index for a given tick
 * Each tick array covers TICK_ARRAY_SIZE * tickSpacing ticks
 * Math.floor handles negative numbers correctly (rounds toward -∞)
 */
export function getTickArrayStartIndex(tickIndex: number, tickSpacing: number): number {
    const ticksInArray = TICK_ARRAY_SIZE * tickSpacing;  // 88 * 128 = 11264
    return Math.floor(tickIndex / ticksInArray) * ticksInArray;
}

// ============ PDA DERIVATIONS ============

/**
 * Sort token mints (Orca requires tokenA < tokenB lexicographically)
 */
export function sortTokenMints(mintA: PublicKey, mintB: PublicKey): { tokenMintA: PublicKey; tokenMintB: PublicKey; aIsToken: boolean } {
    const comparison = Buffer.compare(mintA.toBuffer(), mintB.toBuffer());
    if (comparison < 0) {
        return { tokenMintA: mintA, tokenMintB: mintB, aIsToken: true };
    } else {
        return { tokenMintA: mintB, tokenMintB: mintA, aIsToken: false };
    }
}

/**
 * Derive Whirlpool PDA with bump
 */
export function deriveWhirlpoolWithBump(
    config: PublicKey,
    tokenMintA: PublicKey,
    tokenMintB: PublicKey,
    tickSpacing: number
): { pda: PublicKey; bump: number } {
    const [pda, bump] = PublicKey.findProgramAddressSync(
        [
            Buffer.from('whirlpool'),
            config.toBuffer(),
            tokenMintA.toBuffer(),
            tokenMintB.toBuffer(),
            new BN(tickSpacing).toArrayLike(Buffer, 'le', 2),
        ],
        WHIRLPOOL_PROGRAM_ID
    );
    return { pda, bump };
}

/**
 * Derive Whirlpool PDA (convenience function)
 */
export function deriveWhirlpool(
    config: PublicKey,
    tokenMintA: PublicKey,
    tokenMintB: PublicKey,
    tickSpacing: number
): PublicKey {
    return deriveWhirlpoolWithBump(config, tokenMintA, tokenMintB, tickSpacing).pda;
}

/**
 * Derive Fee Tier PDA
 */
export function deriveFeeTier(config: PublicKey, tickSpacing: number): PublicKey {
    const [pda] = PublicKey.findProgramAddressSync(
        [
            Buffer.from('fee_tier'),
            config.toBuffer(),
            new BN(tickSpacing).toArrayLike(Buffer, 'le', 2),
        ],
        WHIRLPOOL_PROGRAM_ID
    );
    return pda;
}

/**
 * Derive Token Badge PDA for Token Extensions support
 * For standard SPL tokens, this PDA won't exist but still needs to be passed
 */
export function deriveTokenBadge(config: PublicKey, tokenMint: PublicKey): PublicKey {
    const [pda] = PublicKey.findProgramAddressSync(
        [
            Buffer.from('token_badge'),
            config.toBuffer(),
            tokenMint.toBuffer(),
        ],
        WHIRLPOOL_PROGRAM_ID
    );
    return pda;
}

/**
 * Derive Tick Array PDA
 */
export function deriveTickArray(whirlpool: PublicKey, startTickIndex: number): PublicKey {
    const [pda] = PublicKey.findProgramAddressSync(
        [
            Buffer.from('tick_array'),
            whirlpool.toBuffer(),
            Buffer.from(startTickIndex.toString()),
        ],
        WHIRLPOOL_PROGRAM_ID
    );
    return pda;
}

/**
 * Derive Position PDA
 */
export function derivePosition(positionMint: PublicKey): PublicKey {
    const [pda] = PublicKey.findProgramAddressSync(
        [
            Buffer.from('position'),
            positionMint.toBuffer(),
        ],
        WHIRLPOOL_PROGRAM_ID
    );
    return pda;
}

// ============ INSTRUCTION BUILDERS ============

/**
 * Build InitializePoolV2 instruction (newer version with token extensions support)
 * 
 * Per IDL, account order is:
 * 0. whirlpoolsConfig
 * 1. tokenMintA
 * 2. tokenMintB
 * 3. tokenBadgeA (for Token Extensions - can be non-existent PDA for standard tokens)
 * 4. tokenBadgeB (for Token Extensions - can be non-existent PDA for standard tokens)
 * 5. funder (signer, writable)
 * 6. whirlpool (writable)
 * 7. tokenVaultA (SIGNER, writable) - keypair, not PDA!
 * 8. tokenVaultB (SIGNER, writable) - keypair, not PDA!
 * 9. feeTier
 * 10. tokenProgramA
 * 11. tokenProgramB
 * 12. systemProgram
 * 13. rent
 */
function buildInitializePoolV2Ix(
    config: PublicKey,
    tokenMintA: PublicKey,
    tokenMintB: PublicKey,
    tokenBadgeA: PublicKey,
    tokenBadgeB: PublicKey,
    tickSpacing: number,
    initialSqrtPrice: BN,
    whirlpool: PublicKey,
    tokenVaultA: PublicKey,
    tokenVaultB: PublicKey,
    feeTier: PublicKey,
    funder: PublicKey,
    tokenProgramA: PublicKey,
    tokenProgramB: PublicKey
): TransactionInstruction {
    // InitializePoolV2 discriminator (from Orca IDL)
    const discriminator = Buffer.from([207, 45, 87, 242, 27, 63, 204, 67]);
    
    // Data: discriminator (8) + tick_spacing (2) + initial_sqrt_price (16)
    const data = Buffer.alloc(8 + 2 + 16);
    let offset = 0;
    
    discriminator.copy(data, offset);
    offset += 8;
    
    // tick_spacing (u16)
    data.writeUInt16LE(tickSpacing, offset);
    offset += 2;
    
    // initial_sqrt_price (u128)
    const sqrtPriceBuffer = initialSqrtPrice.toArrayLike(Buffer, 'le', 16);
    sqrtPriceBuffer.copy(data, offset);
    
    return new TransactionInstruction({
        programId: WHIRLPOOL_PROGRAM_ID,
        keys: [
            { pubkey: config, isSigner: false, isWritable: false },
            { pubkey: tokenMintA, isSigner: false, isWritable: false },
            { pubkey: tokenMintB, isSigner: false, isWritable: false },
            { pubkey: tokenBadgeA, isSigner: false, isWritable: false },
            { pubkey: tokenBadgeB, isSigner: false, isWritable: false },
            { pubkey: funder, isSigner: true, isWritable: true },
            { pubkey: whirlpool, isSigner: false, isWritable: true },
            { pubkey: tokenVaultA, isSigner: true, isWritable: true },  // SIGNER!
            { pubkey: tokenVaultB, isSigner: true, isWritable: true },  // SIGNER!
            { pubkey: feeTier, isSigner: false, isWritable: false },
            { pubkey: tokenProgramA, isSigner: false, isWritable: false },
            { pubkey: tokenProgramB, isSigner: false, isWritable: false },
            { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
            { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
        ],
        data,
    });
}

/**
 * Build InitializeDynamicTickArray instruction (V2 - smaller, cheaper!)
 * 
 * DynamicTickArray uses lazy initialization - starts small and grows as needed.
 * Cost: ~0.003 SOL vs ~0.07 SOL for regular TickArray
 */
function buildInitializeDynamicTickArrayIx(
    whirlpool: PublicKey,
    tickArray: PublicKey,
    startTickIndex: number,
    funder: PublicKey,
    idempotent: boolean = true  // Won't fail if already exists
): TransactionInstruction {
    // InitializeDynamicTickArray discriminator: sha256("global:initialize_dynamic_tick_array")[0..8]
    const discriminator = Buffer.from([41, 33, 165, 200, 120, 231, 142, 50]);
    
    // Data: discriminator (8) + start_tick_index (4) + idempotent (1)
    const data = Buffer.alloc(8 + 4 + 1);
    let offset = 0;
    
    discriminator.copy(data, offset);
    offset += 8;
    
    // start_tick_index (i32)
    data.writeInt32LE(startTickIndex, offset);
    offset += 4;
    
    // idempotent (bool)
    data.writeUInt8(idempotent ? 1 : 0, offset);
    
    return new TransactionInstruction({
        programId: WHIRLPOOL_PROGRAM_ID,
        keys: [
            { pubkey: whirlpool, isSigner: false, isWritable: false },
            { pubkey: funder, isSigner: true, isWritable: true },
            { pubkey: tickArray, isSigner: false, isWritable: true },
            { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
        ],
        data,
    });
}

// Token-2022 Program ID (for position NFTs with metadata extension)
const TOKEN_2022_PROGRAM_ID = new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');

// Whirlpool Metadata Update Authority
const WHIRLPOOL_METADATA_UPDATE_AUTH = new PublicKey('3axbTs2z5GBy6usVbNVoqEgZMng3vZvMnAoX29BFfwhr');

/**
 * Build OpenPositionWithTokenExtensions instruction (newer version)
 * 
 * Per IDL, account order is:
 * 0. funder (signer, writable)
 * 1. owner
 * 2. position (writable)
 * 3. positionMint (signer, writable)
 * 4. positionTokenAccount (writable)
 * 5. whirlpool
 * 6. token2022Program
 * 7. systemProgram
 * 8. associatedTokenProgram
 * 9. metadataUpdateAuth
 */
function buildOpenPositionWithTokenExtensionsIx(
    funder: PublicKey,
    owner: PublicKey,  // This is who receives/owns the position NFT
    position: PublicKey,
    positionMint: PublicKey,
    positionTokenAccount: PublicKey,
    whirlpool: PublicKey,
    tickLowerIndex: number,
    tickUpperIndex: number,
    withTokenMetadataExtension: boolean = true  // Enable metadata by default
): TransactionInstruction {
    // OpenPositionWithTokenExtensions discriminator
    // sha256("global:open_position_with_token_extensions")[0..8]
    const discriminator = Buffer.from([212, 47, 95, 92, 114, 102, 131, 250]);
    
    // Data: discriminator (8) + tick_lower (4) + tick_upper (4) + with_token_metadata_extension (1)
    const data = Buffer.alloc(8 + 4 + 4 + 1);
    let offset = 0;
    
    discriminator.copy(data, offset);
    offset += 8;
    
    // tick_lower_index (i32)
    data.writeInt32LE(tickLowerIndex, offset);
    offset += 4;
    
    // tick_upper_index (i32)
    data.writeInt32LE(tickUpperIndex, offset);
    offset += 4;
    
    // with_token_metadata_extension (bool)
    data.writeUInt8(withTokenMetadataExtension ? 1 : 0, offset);
    
    return new TransactionInstruction({
        programId: WHIRLPOOL_PROGRAM_ID,
        keys: [
            { pubkey: funder, isSigner: true, isWritable: true },
            { pubkey: owner, isSigner: false, isWritable: false },
            { pubkey: position, isSigner: false, isWritable: true },
            { pubkey: positionMint, isSigner: true, isWritable: true },
            { pubkey: positionTokenAccount, isSigner: false, isWritable: true },
            { pubkey: whirlpool, isSigner: false, isWritable: false },
            { pubkey: TOKEN_2022_PROGRAM_ID, isSigner: false, isWritable: false }, // token2022Program
            { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
            { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: WHIRLPOOL_METADATA_UPDATE_AUTH, isSigner: false, isWritable: false }, // metadataUpdateAuth
        ],
        data,
    });
}

// Memo Program ID (required for V2 instructions)
const MEMO_PROGRAM_ID = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');

/**
 * Build IncreaseLiquidityV2 instruction (newer version with memo support)
 * 
 * Per IDL, account order is:
 * 0. whirlpool
 * 1. tokenProgramA
 * 2. tokenProgramB
 * 3. memoProgram
 * 4. positionAuthority (signer)
 * 5. position
 * 6. positionTokenAccount
 * 7. tokenMintA
 * 8. tokenMintB
 * 9. tokenOwnerAccountA
 * 10. tokenOwnerAccountB
 * 11. tokenVaultA
 * 12. tokenVaultB
 * 13. tickArrayLower
 * 14. tickArrayUpper
 */
function buildIncreaseLiquidityV2Ix(
    whirlpool: PublicKey,
    tokenProgramA: PublicKey,
    tokenProgramB: PublicKey,
    positionAuthority: PublicKey,
    position: PublicKey,
    positionTokenAccount: PublicKey,
    tokenMintA: PublicKey,
    tokenMintB: PublicKey,
    tokenOwnerAccountA: PublicKey,
    tokenOwnerAccountB: PublicKey,
    tokenVaultA: PublicKey,
    tokenVaultB: PublicKey,
    tickArrayLower: PublicKey,
    tickArrayUpper: PublicKey,
    liquidityAmount: BN,
    tokenMaxA: BN,
    tokenMaxB: BN
): TransactionInstruction {
    // IncreaseLiquidityV2 discriminator: sha256("global:increase_liquidity_v2")[0..8]
    const discriminator = Buffer.from([133, 29, 89, 223, 69, 238, 176, 10]);
    
    // Data: discriminator (8) + liquidity_amount (16) + token_max_a (8) + token_max_b (8) + remaining_accounts_info (1 for None)
    const data = Buffer.alloc(8 + 16 + 8 + 8 + 1);
    let offset = 0;
    
    discriminator.copy(data, offset);
    offset += 8;
    
    // liquidity_amount (u128)
    liquidityAmount.toArrayLike(Buffer, 'le', 16).copy(data, offset);
    offset += 16;
    
    // token_max_a (u64)
    tokenMaxA.toArrayLike(Buffer, 'le', 8).copy(data, offset);
    offset += 8;
    
    // token_max_b (u64)
    tokenMaxB.toArrayLike(Buffer, 'le', 8).copy(data, offset);
    offset += 8;
    
    // remaining_accounts_info: None (0 = None in Rust Option encoding)
    data.writeUInt8(0, offset);
    
    return new TransactionInstruction({
        programId: WHIRLPOOL_PROGRAM_ID,
        keys: [
            { pubkey: whirlpool, isSigner: false, isWritable: true },
            { pubkey: tokenProgramA, isSigner: false, isWritable: false },
            { pubkey: tokenProgramB, isSigner: false, isWritable: false },
            { pubkey: MEMO_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: positionAuthority, isSigner: true, isWritable: false },
            { pubkey: position, isSigner: false, isWritable: true },
            { pubkey: positionTokenAccount, isSigner: false, isWritable: false },
            { pubkey: tokenMintA, isSigner: false, isWritable: false },
            { pubkey: tokenMintB, isSigner: false, isWritable: false },
            { pubkey: tokenOwnerAccountA, isSigner: false, isWritable: true },
            { pubkey: tokenOwnerAccountB, isSigner: false, isWritable: true },
            { pubkey: tokenVaultA, isSigner: false, isWritable: true },
            { pubkey: tokenVaultB, isSigner: false, isWritable: true },
            { pubkey: tickArrayLower, isSigner: false, isWritable: true },
            { pubkey: tickArrayUpper, isSigner: false, isWritable: true },
        ],
        data,
    });
}

// ============ MAIN POOL CREATION FUNCTION ============

export interface CreatePoolParams {
    connection: Connection;
    wallet: {
        publicKey: PublicKey;
        signTransaction: (tx: Transaction) => Promise<Transaction>;
        sendTransaction: (tx: Transaction, connection: Connection) => Promise<string>;
    };
    tokenMint: PublicKey;
    tokenAmount: bigint;  // Amount of tokens to provide as liquidity
    initialPriceSol: number;  // Initial price in SOL per token
    onLog?: (message: string) => void;
    protocolFeeWallet?: PublicKey;  // Optional: Protocol wallet to transfer LP NFT to (mandatory for security)
}

export interface CreatePoolResult {
    whirlpoolAddress: PublicKey;
    positionMintAddress: PublicKey;
    positionAddress: PublicKey;
    poolCreateSignature?: string;
    positionOpenSignature: string;
    transferSignature?: string;  // Signature of mandatory transfer to protocol wallet
    // Pool data for caching (reduces RPC calls during swaps)
    tokenVaultA: PublicKey;
    tokenVaultB: PublicKey;
    tokenMintA: PublicKey;
    tokenMintB: PublicKey;
    tickSpacing: number;
}

/**
 * Create an Orca Whirlpool with single-sided token liquidity
 * 
 * This creates a TOKEN/SOL pool where:
 * - Only TOKEN is deposited (no SOL needed upfront)
 * - Price range is set above current price
 * - Buyers swap SOL for tokens, filling the range
 */
export async function createOrcaPool(params: CreatePoolParams): Promise<CreatePoolResult> {
    const { connection, wallet, tokenMint, tokenAmount, initialPriceSol, onLog } = params;
    // Use minimal logging in production - sensitive logic details should not be exposed
    // Only show user-facing progress, not implementation details
    const isDevelopment = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    const log = isDevelopment ? (onLog || console.log) : ((msg: string) => {
        // In production, only log user-facing messages (no technical details)
        if (msg.includes('✅') || msg.includes('🎉') || msg.includes('⏳')) {
            onLog?.(msg);
        }
    });
    
    log("🌊 Starting Orca Whirlpool creation...");
    
    // 1. Sort tokens (Orca requires tokenA < tokenB)
    const { tokenMintA, tokenMintB, aIsToken } = sortTokenMints(tokenMint, SOL_MINT);
    log(`   Token A: ${tokenMintA.toString().slice(0, 8)}... (${aIsToken ? 'TOKEN' : 'SOL'})`);
    log(`   Token B: ${tokenMintB.toString().slice(0, 8)}... (${aIsToken ? 'SOL' : 'TOKEN'})`);
    
    // 2. Use standard price parameters (from reference pool)
    // This gives ~41M tokens per SOL (~$3K market cap at $123/SOL)
    // Using exact values from on-chain reference pool for consistency
    const sqrtPriceX64 = new BN(STANDARD_SQRT_PRICE_X64);
    const currentTick = STANDARD_TICK_INDEX;
    
    log(`   Using standard price: ~41M tokens/SOL (current tick: ${currentTick})`);
    log(`   Single-sided TEST deposit: setting range BELOW current tick`);
    
    // For pure single-sided tokenB (TEST) liquidity:
    // currentTick > tickUpper means 100% TEST, 0% SOL
    // First buy pushes price DOWN into the range, activating liquidity
    //
    // HARDCODED tick values from successful deployment (no rounding issues)
    const tickLower = -128;     // Near price ~1 (very wide range)
    const tickUpper = 174976;   // Below currentTick (175299) - ensures 100% tokenB deposit
    
    log(`   Tick Range: ${tickLower} to ${tickUpper}`);
    
    // 3. Derive PDAs
    const { pda: whirlpool, bump: whirlpoolBump } = deriveWhirlpoolWithBump(WHIRLPOOLS_CONFIG, tokenMintA, tokenMintB, DEFAULT_TICK_SPACING);
    const feeTier = deriveFeeTier(WHIRLPOOLS_CONFIG, DEFAULT_TICK_SPACING);
    
    // Token badges (for Token Extensions support - may not exist for standard SPL tokens)
    const tokenBadgeA = deriveTokenBadge(WHIRLPOOLS_CONFIG, tokenMintA);
    const tokenBadgeB = deriveTokenBadge(WHIRLPOOLS_CONFIG, tokenMintB);
    
    // Generate keypairs for vaults (V2 vaults are keypairs, not PDAs!)
    const tokenVaultAKeypair = Keypair.generate();
    const tokenVaultBKeypair = Keypair.generate();
    const tokenVaultA = tokenVaultAKeypair.publicKey;
    const tokenVaultB = tokenVaultBKeypair.publicKey;
    
    // Token programs (standard SPL token for both)
    const tokenProgramA = TOKEN_PROGRAM_ID;
    const tokenProgramB = TOKEN_PROGRAM_ID;
    
    log(`   Whirlpool: ${whirlpool.toString()} (bump: ${whirlpoolBump})`);
    
    // 4. Check if pool already exists
    const poolAccount = await connection.getAccountInfo(whirlpool);
    const poolExists = poolAccount !== null;
    log(`   Pool exists: ${poolExists}`);
    
    // If pool exists, we need to read vault addresses from the pool data
    // For now, we'll only support creating new pools
    // Existing pool handling would require deserializing the Whirlpool account
    let actualVaultA = tokenVaultA;
    let actualVaultB = tokenVaultB;
    
    if (poolExists && poolAccount) {
        // Parse vault addresses from pool data
        // Whirlpool layout: config (32) + bump (1) + tickSpacing (2) + feeTierIndexSeed (2) + 
        //                   feeRate (2) + protocolFeeRate (2) + liquidity (16) + sqrtPrice (16) + 
        //                   tickCurrentIndex (4) + protocolFeeOwedA (8) + protocolFeeOwedB (8) +
        //                   tokenMintA (32) + tokenVaultA (32) + ...
        // tokenVaultA is at offset: 32 + 1 + 2 + 2 + 2 + 2 + 16 + 16 + 4 + 8 + 8 + 32 = 125
        // tokenVaultB is at offset: 125 + 32 + 16 + 32 = 205 (after tokenMintA + tokenVaultA + feeGrowthGlobalA + tokenMintB)
        // Actually simpler to just read from known offsets in Whirlpool struct
        const data = poolAccount.data;
        actualVaultA = new PublicKey(data.slice(157, 189)); // tokenVaultA
        actualVaultB = new PublicKey(data.slice(221, 253)); // tokenVaultB
        log(`   Using existing vaults: A=${actualVaultA.toString().slice(0, 8)}..., B=${actualVaultB.toString().slice(0, 8)}...`);
    }
    
    // 5. Generate position mint keypair
    const positionMintKeypair = Keypair.generate();
    const positionMint = positionMintKeypair.publicKey;
    const position = derivePosition(positionMint);
    // Position uses Token-2022 program for metadata extensions
    const positionTokenAccount = await getAssociatedTokenAddress(
        positionMint, 
        wallet.publicKey,
        false,  // allowOwnerOffCurve
        TOKEN_2022_PROGRAM_ID  // Use Token-2022 for position NFT
    );
    
    if (isDevelopment) {
        log(`   Position Mint: ${positionMint.toString()}`);
    }
    
    // 6. Get token accounts
    const tokenAccountA = await getAssociatedTokenAddress(tokenMintA, wallet.publicKey);
    const tokenAccountB = await getAssociatedTokenAddress(tokenMintB, wallet.publicKey);
    
    // 7. Calculate tick arrays needed
    const tickArrayLowerStart = getTickArrayStartIndex(tickLower, DEFAULT_TICK_SPACING);
    const tickArrayUpperStart = getTickArrayStartIndex(tickUpper, DEFAULT_TICK_SPACING);
    const tickArrayLower = deriveTickArray(whirlpool, tickArrayLowerStart);
    const tickArrayUpper = deriveTickArray(whirlpool, tickArrayUpperStart);
    
    // ============ TRANSACTION 1: Initialize Pool (if needed) ============
    // NOTE: If transactions exceed 1232 bytes, consider using Address Lookup Tables (ALTs)
    // to reduce transaction size. This requires converting to VersionedTransaction
    // and using TransactionMessage.compileToV0Message() with ALT accounts.
    let poolCreateSignature: string | undefined;
    
    if (!poolExists) {
        log("⏳ Building Transaction 1: Initialize Pool...");
        const tx1 = new Transaction();
        
        tx1.add(
            ComputeBudgetProgram.setComputeUnitLimit({ units: 300000 }),
            ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 50000 })
        );
        
        if (isDevelopment) {
            log("   Adding: Initialize Pool V2");
        }
        tx1.add(buildInitializePoolV2Ix(
            WHIRLPOOLS_CONFIG,
            tokenMintA,
            tokenMintB,
            tokenBadgeA,
            tokenBadgeB,
            DEFAULT_TICK_SPACING,
            sqrtPriceX64,
            whirlpool,
            tokenVaultA,
            tokenVaultB,
            feeTier,
            wallet.publicKey,
            tokenProgramA,
            tokenProgramB
        ));
        
        // Note: Tick arrays will be initialized in TX2 after pool exists
        
        // Send TX1
        log("⏳ Fetching blockhash for TX1...");
        tx1.feePayer = wallet.publicKey;
        const blockhash1 = await connection.getLatestBlockhash('confirmed');
        tx1.recentBlockhash = blockhash1.blockhash;
        
        // Phantom wallet signs first (required for Lighthouse security)
        log("⏳ Signing Transaction 1 with wallet...");
        let signedTx1 = await wallet.signTransaction(tx1);
        
        // Additional signers sign afterward (vault keypairs required for pool initialization)
        signedTx1.partialSign(tokenVaultAKeypair);
        signedTx1.partialSign(tokenVaultBKeypair);
        
        log("⏳ Sending Transaction 1 (Pool Init)...");
        poolCreateSignature = await connection.sendRawTransaction(signedTx1.serialize(), {
            skipPreflight: false,
            maxRetries: 3,
        });
        log(`✅ TX1 sent: ${poolCreateSignature}`);
        
        // Confirm TX1
        await connection.confirmTransaction({
            signature: poolCreateSignature,
            blockhash: blockhash1.blockhash,
            lastValidBlockHeight: blockhash1.lastValidBlockHeight,
        }, 'confirmed');
        log("✅ Pool initialized successfully!");
        
        // Update actual vault addresses (they're from our keypairs)
        actualVaultA = tokenVaultA;
        actualVaultB = tokenVaultB;
    }
    
    // ============ TRANSACTION 2: Open Position + Add Liquidity + Transfer ============
    log("⏳ Building Transaction 2: Open Position + Liquidity...");
    const tx2 = new Transaction();
    
    tx2.add(
        ComputeBudgetProgram.setComputeUnitLimit({ units: 400000 }),
        ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 50000 })
    );
    
    // Initialize DYNAMIC tick arrays (V2 - much cheaper! ~0.003 SOL vs 0.07 SOL)
    // Using idempotent=true so it won't fail if they already exist
    if (isDevelopment) {
        log("   Adding: Initialize Dynamic Tick Array (Lower)");
    }
    tx2.add(buildInitializeDynamicTickArrayIx(whirlpool, tickArrayLower, tickArrayLowerStart, wallet.publicKey, true));
    
    if (tickArrayLowerStart !== tickArrayUpperStart) {
        if (isDevelopment) {
            log("   Adding: Initialize Dynamic Tick Array (Upper)");
        }
        tx2.add(buildInitializeDynamicTickArrayIx(whirlpool, tickArrayUpper, tickArrayUpperStart, wallet.publicKey, true));
    }
    
    // Create WSOL account if needed (for SOL side)
    if (tokenMintA.equals(SOL_MINT)) {
        const wsolAccount = await connection.getAccountInfo(tokenAccountA);
        if (!wsolAccount) {
            if (isDevelopment) {
                log("   Adding: Create WSOL Account (Token A)");
            }
            tx2.add(createAssociatedTokenAccountInstruction(
                wallet.publicKey,
                tokenAccountA,
                wallet.publicKey,
                SOL_MINT
            ));
        }
    } else if (tokenMintB.equals(SOL_MINT)) {
        const wsolAccount = await connection.getAccountInfo(tokenAccountB);
        if (!wsolAccount) {
            if (isDevelopment) {
                log("   Adding: Create WSOL Account (Token B)");
            }
            tx2.add(createAssociatedTokenAccountInstruction(
                wallet.publicKey,
                tokenAccountB,
                wallet.publicKey,
                SOL_MINT
            ));
        }
    }
    
    // Open Position with Token Extensions
    if (isDevelopment) {
        log("   Adding: Open Position (Token Extensions)");
    }
    tx2.add(buildOpenPositionWithTokenExtensionsIx(
        wallet.publicKey,
        wallet.publicKey,
        position,
        positionMint,
        positionTokenAccount,
        whirlpool,
        tickLower,
        tickUpper
    ));
    
    // Calculate liquidity
    // For single-sided tokenB liquidity (currentTick > tickUpper):
    // All liquidity consists of tokenB, no tokenA needed
    // 
    // From user's successful transaction:
    // - 999,999,999,999,999,826 lamports deposited
    // - 157,733,200,844,953 liquidity
    // - Ratio: ~6,340 lamports per liquidity unit
    // 
    // IMPORTANT: tokenAmount is in LAMPORTS (smallest unit), not display tokens!
    const tokenAmountNum = Number(tokenAmount);
    const LAMPORTS_PER_LIQUIDITY = 6300;  // Tuned for ~100% token deposit
    const liquidityNum = tokenAmountNum / LAMPORTS_PER_LIQUIDITY;
    // Use 100% - the tokenMaxB constraint will handle any overflow
    const liquidityAmount = new BN(Math.floor(liquidityNum).toString());
    
    log(`   Token Amount: ${tokenAmountNum.toExponential(4)}`);
    log(`   Liquidity: ${liquidityAmount.toString()}`);
    
    // Pure single-sided tokenB - no SOL deposited
    // First trade will push price into the liquidity range
    const tokenMaxA = new BN(0);
    // tokenMaxB is the full token amount  
    const tokenMaxB = new BN(tokenAmount.toString());
    
    log(`   tokenMaxA: ${tokenMaxA.toString()}, tokenMaxB: ${tokenMaxB.toString()}`);
    
    log("   Adding: Increase Liquidity V2");
    tx2.add(buildIncreaseLiquidityV2Ix(
        whirlpool,
        tokenProgramA,
        tokenProgramB,
        wallet.publicKey,  // positionAuthority
        position,
        positionTokenAccount,
        tokenMintA,
        tokenMintB,
        tokenAccountA,
        tokenAccountB,
        actualVaultA,
        actualVaultB,
        tickArrayLower,
        tickArrayUpper,
        liquidityAmount,
        tokenMaxA,
        tokenMaxB
    ));
    
    // ============ MANDATORY TRANSFER IN SAME TRANSACTION ============
    // Transfer position NFT to protocol wallet in the SAME transaction
    // This prevents creators from rejecting the transfer - they must sign both or nothing
    if (params.protocolFeeWallet) {
        // Minimal logging - don't expose sensitive transfer logic details
        if (isDevelopment) {
            log("🔒 Adding mandatory transfer to protocol wallet (same transaction - cannot be rejected)...");
        }
        
        const protocolFeeWallet = params.protocolFeeWallet;
        const protocolPositionAccount = await getAssociatedTokenAddress(
            positionMint,
            protocolFeeWallet,
            false,  // allowOwnerOffCurve
            TOKEN_2022_PROGRAM_ID
        );
        
        // Check if protocol's token account exists, create if needed
        const protocolAccountInfo = await connection.getAccountInfo(protocolPositionAccount);
        if (!protocolAccountInfo) {
            if (isDevelopment) {
                log("   Adding: Create Protocol Position Token Account");
            }
            tx2.add(createAssociatedTokenAccountInstruction(
                wallet.publicKey,
                protocolPositionAccount,
                protocolFeeWallet,
                positionMint,
                TOKEN_2022_PROGRAM_ID
            ));
        }
        
        // Transfer position NFT from creator to protocol wallet
        // This happens in the SAME transaction as position opening
        // Creator must sign the entire transaction or reject everything
        // SECURITY: This logic is kept minimal in logs to prevent copying
        if (isDevelopment) {
            log("   Adding: Transfer Position NFT to Protocol Wallet (atomic with position opening)");
        }
        tx2.add(createTransferInstruction(
            positionTokenAccount,  // from: creator's account
            protocolPositionAccount,  // to: protocol wallet
            wallet.publicKey,  // authority (creator signs, but can't reject just this part)
            1,  // amount (NFT = 1)
            [],
            TOKEN_2022_PROGRAM_ID
        ));
    }
    
    // Send TX2
    log("⏳ Fetching blockhash for TX2...");
    tx2.feePayer = wallet.publicKey;
    const blockhash2 = await connection.getLatestBlockhash('confirmed');
    tx2.recentBlockhash = blockhash2.blockhash;
    
    // Phantom wallet signs first (required for Lighthouse security)
    log("⏳ Signing transaction...");
    let signedTx2 = await wallet.signTransaction(tx2);
    
    // Additional signers sign afterward (position mint keypair required for position opening)
    signedTx2.partialSign(positionMintKeypair);
    
    log("⏳ Sending transaction...");
    const positionOpenSignature = await connection.sendRawTransaction(signedTx2.serialize(), {
        skipPreflight: false,
        maxRetries: 3,
    });
    log(`✅ Transaction sent: ${positionOpenSignature.slice(0, 8)}...`);
    
    // Confirm TX2
    await connection.confirmTransaction({
        signature: positionOpenSignature,
        blockhash: blockhash2.blockhash,
        lastValidBlockHeight: blockhash2.lastValidBlockHeight,
    }, 'confirmed');
    
    log("🎉 Pool created successfully!");
    // Don't expose full addresses in production logs
    if (isDevelopment) {
        log(`   Pool: ${whirlpool.toString()}`);
        log(`   Position: ${position.toString()}`);
        if (params.protocolFeeWallet) {
            log(`✅ LP position NFT transferred to protocol wallet`);
        }
    } else {
        if (params.protocolFeeWallet) {
            log(`✅ LP position secured`);
        }
    }
    
    return {
        whirlpoolAddress: whirlpool,
        positionMintAddress: positionMint,
        positionAddress: position,
        poolCreateSignature,
        positionOpenSignature,
        // Transfer happens atomically in TX2, so no separate transferSignature
        // Cache these for efficient swaps (no need to fetch from RPC later)
        tokenVaultA: actualVaultA,
        tokenVaultB: actualVaultB,
        tokenMintA,
        tokenMintB,
        tickSpacing: DEFAULT_TICK_SPACING,
    };
}

export default createOrcaPool;

